import { useRef, useState } from 'react';
import { usePostUploadsProfilePictureMutation } from '../../store/backendApi';

interface UseProfilePictureUploadReturn {
  imageVersion: number;
  isUploading: boolean;
  uploadPicture: (file: File) => Promise<void>;
  triggerFileSelect: () => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
}

/**
 * Hook to handle profile picture upload to S3.
 * Platform-agnostic: Upload logic works everywhere, file selection needs platform adapter.
 *
 * @returns Upload state, handlers, and file input ref
 */
export const useProfilePictureUpload = (): UseProfilePictureUploadReturn => {
  const [imageVersion, setImageVersion] = useState(Date.now());
  const [isUploading, setIsUploading] = useState(false);
  const [generateUploadUrl] = usePostUploadsProfilePictureMutation();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const uploadPicture = async (file: File): Promise<void> => {
    setIsUploading(true);
    try {
      // Get presigned S3 upload URL
      const response = await generateUploadUrl({ filename: file.name }).unwrap();
      const uploadUrlObj = response.uploadUrls[0];

      if (!uploadUrlObj) {
        throw new Error('No upload URL received');
      }

      // Upload file to S3
      const uploadResponse = await fetch(uploadUrlObj.uploadUrl, {
        method: 'PUT',
        body: file,
        headers: {
          'Content-Type': file.type,
        },
      });

      if (!uploadResponse.ok) {
        throw new Error('Upload failed');
      }

      // Update image version to force refresh (cache busting)
      setImageVersion(Date.now());
    } catch (error) {
      console.error('Failed to upload profile picture:', error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  const triggerFileSelect = () => {
    fileInputRef.current?.click();
  };

  return {
    imageVersion,
    isUploading,
    uploadPicture,
    triggerFileSelect,
    fileInputRef,
  };
};
